import React from 'react';
import {Control, UseFormRegister} from 'react-hook-form';
import {t} from '@lingui/core/macro';

import Alert from 'shared/ui/Alert/Alert';
import Button from 'shared/ui/Button/Button/Button';
import FormGroup from 'shared/ui/FormGroup/FormGroup';
import HintQuestion from 'shared/ui/Hint/HintQuestion';
import {DeleteButton} from 'shared/ui/IconButton/iconButtons';
import InputText from 'shared/ui/Input/InputText';
import RadioGroup from 'shared/ui/RadioGroup/RadioGroup';
import {
    REQUEST_HEADER_NAME_MAX_LENGTH,
    REQUEST_HEADER_VALUE_MAX_LENGTH,
    REQUEST_HEADERS_MAX,
} from 'features/http/lib/requestHeaders';
import {useRequestHeaders} from 'features/http/model/useRequestHeaders';
import {useHeadersPanelExpanded} from 'features/http/model/useHeadersPanelExpanded';

import SettingsArrow from '../SettingsArrow/SettingsArrow';
import FieldCard from '../CheckboxBlock/FieldCard/FieldCard';
import {getHostLockedHint, getRequestHeadersHint, getUserAgentLockedHint} from '../CheckboxBlock/checkboxBlockHints';

import checkboxStyles from '../CheckboxBlock/CheckboxBlock.module.scss';
import styles from './RequestHeadersField.module.scss';

interface IRequestHeadersFieldProps {
    control: Control<any>,
    register: UseFormRegister<any>,
    selective?: boolean,
    onRemove?: () => void,
}

const RequestHeadersField: React.FC<IRequestHeadersFieldProps> = ({
    control,
    register,
    selective = false,
    onRemove,
}) => {
    const {
        fields,
        isChecked,
        canAdd,
        addRow,
        removeRow,
        nameRules,
        valueRules,
        isUserAgentLocked,
        isHostLocked,
        getNameError,
        error,
        hasErrors,
    } = useRequestHeaders(control, selective);
    const {expanded, toggle} = useHeadersPanelExpanded();
    const isOpen = selective ? isChecked : expanded || hasErrors;

    const label = t`Заголовки запроса`;
    const hint = getRequestHeadersHint(REQUEST_HEADERS_MAX);

    const content = isOpen && (
        <div className={styles.content}>
            {fields.map((field, index) => {
                const nameError = getNameError(index);

                return (
                    <div key={field.id} className={styles.row}>
                        <div className={styles.inputs}>
                            <div className={styles.fields}>
                                <InputText
                                    name={`headers.${index}.name`}
                                    register={register}
                                    rules={nameRules(index)}
                                    control={control}
                                    placeholder={t`Имя`}
                                    maxLength={REQUEST_HEADER_NAME_MAX_LENGTH}
                                />
                                <InputText
                                    name={`headers.${index}.value`}
                                    register={register}
                                    rules={valueRules(index)}
                                    control={control}
                                    placeholder={t`Значение`}
                                    maxLength={REQUEST_HEADER_VALUE_MAX_LENGTH}
                                />
                            </div>
                            <DeleteButton
                                title={t`Удалить заголовок`}
                                aria-label={t`Удалить заголовок`}
                                onClick={() => removeRow(index)}
                            />
                        </div>
                        {nameError && <Alert>{nameError}</Alert>}
                        {isUserAgentLocked(index) && (
                            <p className={checkboxStyles.methodHint}>
                                {getUserAgentLockedHint()}
                            </p>
                        )}
                        {isHostLocked(index) && (
                            <p className={checkboxStyles.methodHint}>
                                {getHostLockedHint()}
                            </p>
                        )}
                    </div>
                );
            })}

            <Button
                variant="transparent"
                className={styles.add}
                disabled={!canAdd}
                onClick={addRow}
            >
                {t`+ Добавить заголовок`}
            </Button>
        </div>
    );

    if (selective) {
        return (
            <FieldCard selective freeTariff={false} onRemove={onRemove}>
                <label className={checkboxStyles.labelArray}>
                    {label}
                    <HintQuestion placement="top-start">{hint}</HintQuestion>
                </label>
                <FormGroup className={checkboxStyles.formGroupRadio}>
                    <RadioGroup
                        name="headers_mode"
                        options={[
                            {
                                value: 'replace',
                                label: t`Заменить`,
                                hint: t`Заголовки выбранных сайтов будут заменены указанными.`,
                            },
                            {
                                value: 'clear',
                                label: t`Очистить`,
                                hint: t`У выбранных сайтов будут удалены все заголовки запроса.`,
                            },
                        ]}
                        required
                        control={control}
                    />
                </FormGroup>
                {content}
                {error && <Alert>{error}</Alert>}
            </FieldCard>
        );
    }

    return (
        <FormGroup className={styles.panel}>
            <div className={styles.header}>
                <label className={styles.label}>
                    {label}
                    <HintQuestion placement="top-start">{hint}</HintQuestion>
                </label>
                <SettingsArrow isOpen={isOpen} onClick={toggle}/>
            </div>
            {content}
            {error && <Alert>{error}</Alert>}
        </FormGroup>
    );
};

export default RequestHeadersField;
